import React, {memo, useState} from 'react'
import {Formik, Form} from 'formik'
import {useParams, useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import * as Yup from 'yup'
import {v4 as uuidv4} from 'uuid'
import UpdateContainer from '../common/updateContainer'
import {Input, Label} from '../../../component'
import {addBenefit, clearBenefitDetail, updateBenefit} from '../../../store/actions'
import {RootState} from '../../../store/slices'
import {useAppDispatch, useSelector} from '../../../store'
import {EventType, BenefitType, FormActionType} from '../type'
import {serializeFormData} from '../../../functions/utils'
import {mediasModel} from '../../../data'

const BenefitsUpdate = () => {
  const {t} = useTranslation()
  const navigation = useNavigate()
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(false)
  const {benefitDetail} = useSelector(({benefit}: RootState) => ({
    benefitDetail: benefit.benefitDetail,
  }))
  const params = useParams()
  const id = (params?.id && parseInt(params.id, 10) && benefitDetail.id) || undefined
  if (!id) {
    dispatch(clearBenefitDetail())
  }
  const initialAddValues = {
    title: '',
    medias_model: mediasModel,
    description: '',
  }
  const initialUpdateValues = {
    title: benefitDetail?.title || '',
    medias_model:
      (benefitDetail?.medias_model &&
        benefitDetail.medias_model?.length > 0 &&
        benefitDetail.medias_model[0]) ||
      mediasModel,
    description: benefitDetail?.description || '',
  }
  const onSubmit = async (values: BenefitType) => {
    setLoading(true)
    const formValues = {
      title: values.title || undefined,
      description: values.description || undefined,
      icon: values.medias_model.file ? values.medias_model.file : undefined,
    }
    const formData = serializeFormData(formValues)
    if (id) {
      const response = await dispatch(updateBenefit(id, formData))
      if (response) navigation(`/content-management`)
      setLoading(false)
    } else {
      const response = await dispatch(addBenefit(formData))
      if (response) navigation(`/content-management`)
      setLoading(false)
    }
  }
  const validationSchema = Yup.object().shape({
    title: Yup.string().trim().required('Title cannot be empty.'),
    medias_model: Yup.mixed().test(t('fileRequired'), t('mediaFilesCannotBeEmpty'), (value) => {
      if (!value || !value.original_url) return false
      return true
    }),
  })

  const onClickListAction = ({command, setValues, submitForm}: FormActionType<BenefitType>) => {
    if (command === 'cancel') {
      navigation(`/content-management`)
    } else if (command === 'remove') {
      setValues(initialAddValues)
    } else if (command === 'save' || command === 'update') {
      submitForm()
    }
  }

  return (
    <Formik
      initialValues={benefitDetail?.id ? initialUpdateValues : initialAddValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({errors, touched, submitForm, setFieldValue, setValues, values}) => (
        <Form className="h-100">
          <UpdateContainer
            btn3Title={id ? 'remove' : undefined}
            btn2Title="cancel"
            btn1Title={id ? 'update' : 'save'}
            title={id ? 'editBenefitsOfGDX' : 'addBenefitsOfGDX'}
            loading={loading}
            onClick={(command: string) => onClickListAction({command, setValues, submitForm})}
          >
            <div className="faqs-edit-box">
              <div className="flex f-0-3">
                <div className="mr-3 f-1">
                  <div className="c-form-group">
                    <Label label={t('icon')} className="fs-15" />
                    <div className="flex-center-v flex-d-row">
                      <div className="mr-3">
                        <div className="img_preview-testimonials-inner mt-1 border-light-gray">
                          {values?.medias_model?.original_url && (
                            <img
                              src={values.medias_model.original_url}
                              alt=""
                              className="img_preview-testimonials-inner"
                            />
                          )}
                        </div>
                      </div>
                      <label
                        htmlFor="formId"
                        className="mb-0"
                        onChange={(e: any) => {
                          setFieldValue('medias_model', {
                            id: uuidv4(),
                            file_name: e.target.files[0].name,
                            file: e.target.files[0],
                            original_url: URL.createObjectURL(e.target.files[0]),
                          })
                        }}
                      >
                        <input
                          name=""
                          type="file"
                          id="formId"
                          hidden
                          accept="image/png, image/jpeg"
                        />
                        <span className="c-pointer">
                          <span className="icon-edit-text">
                            {values.medias_model && values.medias_model.file_name
                              ? values.medias_model.file_name
                              : t('chooseFileToUpload')}
                          </span>{' '}
                          <i className="c-icon c-icon--edit-red" />
                        </span>
                      </label>
                    </div>
                    {touched.medias_model && errors.medias_model && (
                      <div className="invalid-feedback">{errors.medias_model as string}</div>
                    )}
                  </div>
                  <Input
                    name="title"
                    label={t('heading')}
                    onChange={(e: EventType) => {
                      setFieldValue('title', e.target.value)
                    }}
                    placeholder={t('enterHeadingHere')}
                    error={touched.title && errors.title}
                    className="form-control mt-1"
                    classNameLable="fs-15"
                  />
                  <Input
                    name="description"
                    label={t('bodyText')}
                    onChange={(e: EventType) => {
                      setFieldValue('description', e.target.value)
                    }}
                    component="textarea"
                    placeholder={t('enterDescriptionHere')}
                    error={touched.description && errors.description}
                    className="form-control mt-1"
                    classNameLable="fs-15"
                  />
                </div>
              </div>
            </div>
          </UpdateContainer>
        </Form>
      )}
    </Formik>
  )
}

export default memo(BenefitsUpdate)
